import type { ReactNode } from 'react';
import { cn } from '../lib/cn';

export type AuthLayoutProps = {
  /** Page title e.g. "Sign in" */
  title: string;
  /** Optional supporting line under the title */
  subtitle?: string;
  children: ReactNode;
  /** Links under the form (forgot password, sign up) */
  footer?: ReactNode;
  /** Brand mark above the title */
  logo?: ReactNode;
  /**
   * `plain` — form on app background (recommended default).
   * `card` — white surface with light border (optional containment).
   */
  variant?: 'plain' | 'card';
  className?: string;
  /** Narrow legal / support line at bottom of viewport */
  legal?: ReactNode;
};

/**
 * ShellAuth — centered narrow pre-auth layout.
 * No app nav. Calm product voice (not marketing hero).
 */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
  logo,
  variant = 'plain',
  className,
  legal,
}: AuthLayoutProps) {
  return (
    <div
      className={cn(
        'flex min-h-dvh flex-col bg-ds-app text-ds-text',
        className,
      )}
    >
      <div className="flex flex-1 flex-col items-center justify-center px-4 py-10 sm:px-6">
        <div className="w-full max-w-[22rem]">
          {logo ? (
            <div className="mb-6 flex justify-center sm:mb-8">{logo}</div>
          ) : null}

          <div
            className={cn(
              variant === 'card' &&
                'rounded-ds-lg border border-ds-border bg-ds-surface p-6 shadow-ds-sm',
            )}
          >
            <header className={cn(variant === 'card' ? 'mb-5' : 'mb-6 text-center')}>
              <h1 className="text-ds-xl font-semibold tracking-tight text-ds-text">
                {title}
              </h1>
              {subtitle ? (
                <p
                  className={cn(
                    'mt-1.5 text-ds-sm text-ds-muted',
                    variant === 'plain' && 'text-center',
                  )}
                >
                  {subtitle}
                </p>
              ) : null}
            </header>

            <div className="space-y-4">{children}</div>

            {footer ? (
              <div
                className={cn(
                  'mt-6 border-t border-ds-border pt-4 text-ds-sm text-ds-muted',
                  variant === 'plain' && 'text-center',
                )}
              >
                {footer}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {legal ? (
        <div className="px-4 pb-6 text-center text-ds-xs text-ds-muted">{legal}</div>
      ) : null}
    </div>
  );
}

/** Compact brand mark for auth header (replace with real logo asset in product). */
export function AuthLogoMark({
  label = 'Pixel Servicing',
  className,
}: {
  label?: string;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center gap-2', className)}>
      <div
        className="flex size-10 items-center justify-center rounded-ds-lg bg-ds-brand text-ds-sm font-semibold text-ds-inverse"
        aria-hidden
      >
        PS
      </div>
      <span className="text-ds-xs font-medium text-ds-muted">{label}</span>
    </div>
  );
}
